import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TdDataTableService, TdDataTableSortingOrder, ITdDataTableSortChangeEvent, ITdDataTableColumn, TdDialogService, TdLoadingService } from '@covalent/core';
import { Subscription } from 'rxjs/Subscription';
import { Observable } from 'rxjs/Observable';
import { IPageChangeEvent } from '@covalent/core';

import { InventoryService } from '../inventory.service';
import { Piece } from '../piece';
import { Location } from '../../accounting/location';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import { LocationService } from 'services/location.service';
import { ProductService } from '../../purchasing/products/product.service';
import { SettingsService } from '../../../shared/services/settings.service';
import { CollectionService } from '../../../shared/services/collection.service';
import { PieceHistory } from '../piece-history';
import { Utils } from '../../../shared/utils';
import { Grade } from '../../accounting/grade';
import { GradeService } from '../../../shared/services/grade.service';

@Component({
  selector: 'app-inventory-piece',
  templateUrl: './inventory-piece.component.html',
  styleUrls: ['./inventory-piece.component.scss'],
  providers: [CollectionService, ProductService]
})
export class InventoryPieceComponent implements OnInit {
  _id: number;
  _piece: Piece = new Piece;
  _subscription: Subscription = new Subscription();
  _historySubscription: Subscription = new Subscription();
  _grade: Grade = new Grade;
  _allLocations: Location[];

  historyEntries: PieceHistory[];
  _subscriptions: Subscription[] = [];

  columns: ITdDataTableColumn[] = [
    { name: 'last_updated', label: 'Updated' },
    { name: 'color_name', label: 'Color' },
    { name: 'design_name', label: 'Design' },
    { name: 'collection_name', label: 'Collection' },
    { name: 'lot_number', label: 'Lot #' },
    { name: 'grade_external_id', label: 'Grade' },
    { name: 'length', label: 'Length' },
    { name: 'weight', label: 'Weight' },
    { name: 'location_name', label: 'Location' },
    { name: 'branch_code', label: 'Branch' },
    { name: 'deleted', label: 'Deleted?' },
    { name: 'sold', label: 'Sold?' },
    { name: 'defect', label: 'Defect?' },
    { name: 'defect_comment', label: 'Defect Comment' },
    { name: 'updated_by_username', label: 'Username' },
  ];

  allCollections$: Observable<{ id: number, name: string }[]>;
  allDesigns$: Observable<{ id: number, name: string }[]>;
  allColors$: Observable<{ id: number, name: string }[]>;
  allGrades$: Observable<{ id: number, name: string }[]>;

  filteredData: any[];
  filteredTotal: number;
  fromRow: number = 1;
  currentPage: number = 1;
  sortBy: string = 'last_updated';
  sortOrder: TdDataTableSortingOrder = TdDataTableSortingOrder.Descending;
  selectedRows: any[] = [];

  _pieceNumber: string;

  constructor(private _dataTableService: TdDataTableService,
    private _dialogService: TdDialogService,
    private _inventoryService: InventoryService,
    private _productService: ProductService,
    private _locationService: LocationService,
    private _gradeService: GradeService,
    private _loadingService: TdLoadingService,
    public _settingsService: SettingsService,
    private _collectionService: CollectionService,
    public _authService: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit() {

    this.allCollections$ = this._collectionService.getCollections();

    this.route.params.subscribe(
      params => {
        if (params['id']) {
          this._id = params['id'];
          this.loadByID(this._id);
        }
      }
    );

    this.allGrades$ = this._gradeService.getGrade();

  }

  load(): void {

    this._loadingService.register('loading');

    console.log("Getting ID for piece: " + this._pieceNumber);
    this._subscription = this._inventoryService.getPieceByPieceNumber(this._pieceNumber)
      .subscribe(response => {
        this._loadingService.resolve('loading');

        if (response['id'] && response['id'] != 0) {
          console.log("Found piece ID: " + response['id']);

          this.loadByID(response['id']);
        } else {
          console.log("No piece found with piece number: " + this._pieceNumber);
          this._dialogService.openAlert({ title: 'Error!', message: "No piece found with piece number: " + this._pieceNumber });
        }
      });

  }

  loadByID(id: number) {
    console.log("Loading piece: " + id);

    this._loadingService.register('loading');

    this._subscription = this._inventoryService.getPiece(id, true)
      .subscribe(
        (pieces: Piece[]) => {

          if (Array.isArray(pieces) && pieces.length > 0) {
            this._piece = pieces[0];
            this._pieceNumber = this._piece.piece_number;

            //Loction id is a string and it should be a number
            this._piece.location_id = Number.parseInt(this._piece.location_id + '');

            // got the piece, now get all the available locations from this piece's branch
            this._locationService.getLocations(pieces[0].branch_id + '')
              .subscribe((locations: Location[]) => {

                if (Array.isArray(locations)) {
                  this._allLocations = locations;
                } else {
                  this._allLocations = new Array<Location>();
                }

              });

            // Load the right designs
            this.collectionChanged(this._piece.collection_id);
            this.designChanged(this._piece.design_id);

            // load the history
            this._loadingService.register('history');

            this._historySubscription = this._inventoryService
              .getPieceHistory(this._piece.piece_number)
              .subscribe(
                (history: PieceHistory[]) => {

                  if (Array.isArray(history)) {
                    console.log("history retrieved: " + history.length + " entries");
                    this.historyEntries = history;
                  } else {
                    console.log("no history retrieved");
                    this.historyEntries = [];
                  }
                  this.filteredData = this.historyEntries;
                  this.filteredTotal = this.historyEntries.length;
                  this.filter();

                  this._loadingService.resolve('history');
                  this._loadingService.resolve('loading');
                },
                error => {
                  this._dialogService.openAlert({ title: 'Error!', message: 'Unable to load piece history ' + error });
                  console.log("Unable to load piece history" + error);
                  this._loadingService.resolve('history');
                  this._loadingService.resolve('loading');
                }
              );

          } else {
            this._dialogService.openAlert({ title: 'Error!', message: 'Unable to load piece' });
            console.log("Unable to load piece");
            this._loadingService.resolve('loading');
            this._piece = new Piece();
          }

        },

        error => {
          this._dialogService.openAlert({ title: 'Error!', message: 'Unable to load piece ' + error });
          console.log("Unable to load piece");
          this._loadingService.resolve('loading');
        }
      );
  }

  ngOnDestroy(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  delete() {
  }

  page(pagingEvent: IPageChangeEvent): void {
    this.fromRow = pagingEvent.fromRow;
    this.currentPage = pagingEvent.page;
    this._settingsService.setPageSize(pagingEvent.pageSize);
    this.filter();
  }

  filter(): void {
    let newData: any[] = this.historyEntries;
    let excludedColumns: string[] = this.columns
      .filter((column: ITdDataTableColumn) => {
        return ((column.filter === undefined && column.hidden === true) ||
          (column.filter !== undefined && column.filter === false));
      }).map((column: ITdDataTableColumn) => {
        return column.name;
      });

    this.filteredTotal = newData.length;
    newData = Utils.sortData(newData, this.sortBy, this.sortOrder);
    if (this._settingsService.getPageSize() + '' != 'All') {
      newData = this._dataTableService.pageData(newData, this.fromRow, this.currentPage * this._settingsService.getPageSize());
    }
    this.filteredData = newData;
  }

  update() {
    this._inventoryService.updatePiece(this._piece).subscribe(
      response => {
        if (response['status'] == 'SUCCESS') {
          this._dialogService.openAlert({ title: 'Success!', message: 'Piece updated successfully!' }).afterClosed().subscribe(
            result => {
              this.router.navigate(['/inventory', 'list', 'pieces', this._piece.branch_id, this._piece.color_id])
            }
          );
        } else {
          this._dialogService.openAlert({ title: 'Error!', message: 'Could not update piece!' });
        }
      }
    );
  }

  collectionChanged($event) {
    this.allDesigns$ = this._productService.getDesignsPerCollection($event);
  }

  designChanged($event) {
    this.allColors$ = this._productService.getColorsPerDesign($event);
  }

  colorChanged($event) {
    this._piece.color_id = $event;
  }

}



// WEBPACK FOOTER //
// C:/Users/orion/Projects/simplicityEngine/valmust/valmustfe/src/app/agatextscreens/inventory/inventory-piece/inventory-piece.component.ts